import { useCallback, useEffect } from 'react'

export function useMenuKeyboard({
  isOpen,
  onOpen,
  onClose,
  menuRef,
  triggerRef,
  menuContainerRef,
  closeOnOutsideClick = true,
}) {
  const getMenuItems = useCallback(() => {
    if (!menuRef.current) return []
    return Array.from(menuRef.current.querySelectorAll('[role="menuitem"]'))
  }, [menuRef])

  const resetMenuItemTabindex = useCallback(() => {
    getMenuItems().forEach((item) => {
      item.tabIndex = -1
    })
  }, [getMenuItems])

  const setActiveMenuItem = useCallback(
    (item) => {
      getMenuItems().forEach((el) => {
        el.tabIndex = -1
      })
      if (item) {
        item.tabIndex = 0
        item.focus()
      }
    },
    [getMenuItems],
  )

  const open = useCallback(
    (focusTarget = 'first') => {
      onOpen()
      requestAnimationFrame(() => {
        const items = getMenuItems()
        if (items.length === 0) return
        const item = focusTarget === 'last' ? items[items.length - 1] : items[0]
        setActiveMenuItem(item)
      })
    },
    [onOpen, getMenuItems, setActiveMenuItem],
  )

  const close = useCallback(
    (returnFocus = true) => {
      onClose()
      resetMenuItemTabindex()
      if (returnFocus && triggerRef.current) {
        triggerRef.current.focus()
      }
    },
    [onClose, resetMenuItemTabindex, triggerRef],
  )

  const handleTriggerKeyDown = useCallback(
    (event) => {
      if (isOpen) return

      const { key } = event

      if (key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault()
        open(key === 'ArrowUp' ? 'last' : 'first')
        return
      }

      if (key === 'Enter' || key === ' ') {
        event.preventDefault()
        open('first')
      }
    },
    [isOpen, open],
  )

  const handleMenuItemKeyDown = useCallback(
    (event) => {
      if (!isOpen) return

      const items = getMenuItems()
      const currentIndex = items.indexOf(event.target)
      if (currentIndex === -1) return

      const { key } = event

      if (key === 'ArrowDown') {
        event.preventDefault()
        const next = items[currentIndex + 1]
        if (next) setActiveMenuItem(next)
        return
      }

      if (key === 'ArrowUp') {
        event.preventDefault()
        const prev = items[currentIndex - 1]
        if (prev) {
          setActiveMenuItem(prev)
        } else if (triggerRef.current) {
          resetMenuItemTabindex()
          triggerRef.current.focus()
        }
        return
      }

      if (key === 'Home') {
        event.preventDefault()
        setActiveMenuItem(items[0])
        return
      }

      if (key === 'End') {
        event.preventDefault()
        setActiveMenuItem(items[items.length - 1])
        return
      }

      if (key === 'Tab') {
        const isLast = currentIndex === items.length - 1
        const isFirst = currentIndex === 0
        if ((!event.shiftKey && isLast) || (event.shiftKey && isFirst)) {
          close(false)
        }
      }
    },
    [
      isOpen,
      getMenuItems,
      setActiveMenuItem,
      resetMenuItemTabindex,
      triggerRef,
      close,
    ],
  )

  useEffect(() => {
    if (!isOpen) return undefined

    const handleEscape = (event) => {
      if (event.key !== 'Escape') return

      const container = menuContainerRef?.current
      const focusInMenu =
        triggerRef.current === document.activeElement ||
        menuRef.current?.contains(document.activeElement) ||
        container?.contains(document.activeElement)

      if (focusInMenu) {
        event.preventDefault()
        close(true)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, close, menuRef, triggerRef, menuContainerRef])

  useEffect(() => {
    if (!isOpen || !closeOnOutsideClick) return undefined

    const handleClickOutside = (event) => {
      const container = menuContainerRef?.current
      if (container && !container.contains(event.target)) {
        close(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, close, menuContainerRef, closeOnOutsideClick])

  return {
    open,
    close,
    handleTriggerKeyDown,
    handleMenuItemKeyDown,
  }
}
